import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { cn } from "@/lib/utils";
import { Check, Copy, Hash, ChevronDown, ChevronUp } from "lucide-react";

interface MarkdownRendererProps {
  content: string;
  className?: string;
  variant?: "full" | "compact";
}

const COLLAPSE_THRESHOLD = 10; // Lines before collapsing

const CodeBlock = ({ language, code }: { language: string; code: string }) => {
  const [copied, setCopied] = useState(false);
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const lineCount = code.split("\n").length;
  const isLongCode = lineCount > COLLAPSE_THRESHOLD;
  const displayCode = isLongCode && !isExpanded 
    ? code.split("\n").slice(0, COLLAPSE_THRESHOLD).join("\n")
    : code;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Format language name for display
  const displayLanguage = language === "text" ? null : language.charAt(0).toUpperCase() + language.slice(1);

  return (
    <div className="relative group my-2">
      {/* Language badge */}
      {displayLanguage && (
        <div className="absolute left-2 top-2 px-2 py-0.5 rounded text-[10px] font-medium bg-primary/20 text-primary z-10">
          {displayLanguage}
        </div>
      )}
      
      <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <button
          onClick={() => setShowLineNumbers(!showLineNumbers)}
          className={cn(
            "p-1.5 rounded-md bg-secondary/80 hover:bg-secondary transition-colors",
            showLineNumbers ? "text-primary" : "text-muted-foreground hover:text-foreground"
          )}
          aria-label={showLineNumbers ? "Hide line numbers" : "Show line numbers"}
          title={showLineNumbers ? "Hide line numbers" : "Show line numbers"}
        >
          <Hash className="w-4 h-4" />
        </button>
        <button
          onClick={handleCopy}
          className="p-1.5 rounded-md bg-secondary/80 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Copy code"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>
      </div>
      
      <div className={cn(
        "relative overflow-hidden",
        isLongCode && !isExpanded && "max-h-[280px]"
      )}>
        <SyntaxHighlighter
          style={oneDark}
          language={language}
          PreTag="div"
          showLineNumbers={showLineNumbers}
          lineNumberStyle={{
            minWidth: "2.5em",
            paddingRight: "1em",
            color: "rgba(255,255,255,0.3)",
            userSelect: "none",
          }}
          customStyle={{
            margin: 0,
            borderRadius: isLongCode && !isExpanded ? "0.5rem 0.5rem 0 0" : "0.5rem",
            fontSize: "0.875rem",
          }}
        >
          {displayCode}
        </SyntaxHighlighter>
        
        {isLongCode && !isExpanded && (
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#282c34] to-transparent pointer-events-none" />
        )}
      </div>
      
      {isLongCode && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-center gap-1.5 py-2 bg-[#282c34] text-muted-foreground hover:text-foreground text-sm rounded-b-lg border-t border-white/10 transition-colors"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Collapse ({lineCount} lines)
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Expand ({lineCount} lines)
            </>
          )}
        </button>
      )}
    </div>
  );
};

export const MarkdownRenderer = ({ content, className, variant = "full" }: MarkdownRendererProps) => {
  return (
    <div className={cn(
      variant === "compact" && "[&_p]:my-1 [&_h1]:my-2 [&_h2]:my-2 [&_h3]:my-2",
      className
    )}>
      <ReactMarkdown
        components={{
          h1: ({ children }) => (
            <h1 className="text-xl font-bold text-foreground mt-4 mb-2 first:mt-0">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-lg font-semibold text-foreground mt-3 mb-2 first:mt-0">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-base font-semibold text-foreground mt-3 mb-1 first:mt-0">{children}</h3>
          ),
          p: ({ children }) => (
            <p className="text-muted-foreground leading-relaxed my-2 first:mt-0 last:mb-0">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside text-muted-foreground my-2 space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside text-muted-foreground my-2 space-y-1">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="text-muted-foreground">{children}</li>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-foreground">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic">{children}</em>
          ),
          code: ({ className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || "");
            const codeString = String(children).replace(/\n$/, "");
            
            const isInline = !match && !codeString.includes("\n");
            
            if (isInline) {
              return (
                <code className="px-1.5 py-0.5 bg-secondary text-foreground rounded text-sm font-mono" {...props}>
                  {children}
                </code>
              );
            }
            
            return <CodeBlock language={match ? match[1] : "text"} code={codeString} />;
          },
          pre: ({ children }) => (
            <>{children}</>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary/30 pl-4 my-2 text-muted-foreground italic">
              {children}
            </blockquote>
          ),
          a: ({ href, children }) => (
            <a 
              href={href} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {children}
            </a>
          ),
          hr: () => (
            <hr className="border-border my-4" />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
